import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const CODE_LENGTH = 6;

/**
 * CodeVerifyStep — 6-digit verification code input with auto-advance and paste support.
 *
 * @param {Object} props
 * @param {string} props.email - Email the code was sent to
 * @param {function} props.onVerify - Called with the complete code
 * @param {function} props.onBack - Called to go back
 * @param {function} props.onResend - Called to resend the code
 * @param {string|null} [props.deliveryError] - Error shown when initial delivery failed
 * @returns {JSX.Element}
 */
function CodeVerifyStep({ email, onVerify, onBack, onResend, deliveryError = null }) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(deliveryError ? 0 : 30);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (deliveryError) {
      setResendTimer(0);
    }
  }, [deliveryError]);

  // Resend countdown timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Auto-submit when all digits are filled
  const submitCode = useCallback(async (code) => {
    setLoading(true);
    setError(null);
    try {
      await onVerify(code);
    } catch {
      setError('Invalid code. Please try again.');
      setLoading(false);
    }
  }, [onVerify]);

  const handleChange = (index, value) => {
    // Only accept single digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    if (error) setError(null);

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (digit && index === CODE_LENGTH - 1) {
      const code = newDigits.join('');
      if (code.length === CODE_LENGTH) {
        submitCode(code);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pastedData) return;

    const newDigits = [...digits];
    for (let i = 0; i < pastedData.length; i++) {
      newDigits[i] = pastedData[i];
    }
    setDigits(newDigits);

    // Focus last filled input or next empty
    const nextIndex = Math.min(pastedData.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();

    // Auto-submit if complete
    if (pastedData.length === CODE_LENGTH) {
      submitCode(pastedData);
    }
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    setResendTimer(30);
    setDigits(Array(CODE_LENGTH).fill(''));
    setError(null);
    inputRefs.current[0]?.focus();
    onResend?.();
  };

  return (
    <div className="auth-step code-verify-step">
      <button type="button" className="auth-step__back" onClick={onBack} aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </button>

      <h2 className="auth-step__title">Check your email</h2>
      <p className="auth-step__subtitle">
        {deliveryError ? (
          <>We could not send a 6-digit code to <strong>{email}</strong> yet.</>
        ) : (
          <>We sent a 6-digit code to <strong>{email}</strong></>
        )}
      </p>

      <div className="auth-step__form">
        <div className="code-inputs">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              className={`code-input ${error ? 'code-input--error' : ''} ${digit ? 'code-input--filled' : ''}`}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              maxLength={1}
              inputMode="numeric"
              autoFocus={index === 0}
              disabled={loading}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {deliveryError && <p className="auth-input__error code-verify-step__error">{deliveryError}</p>}
        {error && <p className="auth-input__error code-verify-step__error">{error}</p>}
        {loading && <p className="auth-input__checking">Verifying...</p>}

        <button
          type="button"
          className="code-verify-step__resend"
          onClick={handleResend}
          disabled={resendTimer > 0}
        >
          {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
        </button>
      </div>
    </div>
  );
}

CodeVerifyStep.propTypes = {
  email: PropTypes.string.isRequired,
  onVerify: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onResend: PropTypes.func,
  deliveryError: PropTypes.string,
};

export default CodeVerifyStep;
